import { useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux"
import * as handlers from "../../../../../_redux/selected-user/selectedUserHandlers";
import { NavLink } from "react-router-dom";
import Select from 'react-select';
import { Permission } from "../../../../../../Common/layout/Permission";
import { Permissions } from "../../../../../../../util/Permissions";
import SVG from "react-inlinesvg"
import { toAbsoluteUrl } from "../../../../../../../../_metronic/_helpers";
import { useGetTroveNextProfilesQuery } from "../../../../../../../../redux/apiSlice";

// account.name / account.details.name for a Trove Next (custodial/minor) account
// is the PORTFOLIO's product name (e.g. "NG portfolio (Innova)"), not the minor's
// name - every custodial account under one product looks identical in the picker.
// The minor's real name lives on their Trove Next profile record instead, keyed
// by troveNextProfileId, so it's resolved separately below and used in place of
// that generic product name.
const getTroveNextProfileId = (acc) =>
    acc?.meta?.troveNextProfileId
    ?? acc?.details?.meta?.troveNextProfileId
    ?? acc?.troveNextProfileId
    ?? acc?.details?.troveNextProfileId;

const getTroveNextProfileRecordId = (profile) =>
    profile?.profileId || profile?.id || profile?.profile_id || profile?.profileID;

// The account record itself already carries the minor's real name at
// meta.beneficiaryName (confirmed against a live account response) - that's
// a direct, single-request source and should always win over the joined
// Trove Next Profiles lookup below, which depends on troveNextProfileId
// matching a record in a separate paginated list.
const getTroveNextBeneficiaryName = (acc) =>
    acc?.meta?.beneficiaryName
    ?? acc?.details?.meta?.beneficiaryName
    ?? acc?.beneficiaryName
    ?? acc?.details?.beneficiaryName
    ?? "";

const getTroveNextProfileRealName = (profile) =>
    profile?.username
    || profile?.name
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(" ")
    || profile?.fullName
    || "";

// acc.name looks like "NG portfolio (Innova)" - the "(Innova)" tail identifies
// the underlying provider/product variant and is worth keeping, but "NG portfolio"
// itself is generic and identical across every custodial account on that product.
// Swapping it for the minor's first name (e.g. "Ayomide (Innova)") keeps the
// useful part and drops the part that made every row in the picker look the same.
const getProductSuffix = (name) => {
    const match = `${name ?? ""}`.match(/\(([^)]+)\)\s*$/);
    return match ? match[1] : "";
};

// A US corporate account carries the corporate application it was created from
// in meta.applicationId, and its `name` is the company's account name
// (e.g. "TORBRIDGE CAPITAL").
const getCorporateApplicationId = (acc) =>
    acc?.meta?.applicationId ?? acc?.details?.meta?.applicationId;

const getFirstName = (fullName) => `${fullName ?? ""}`.trim().split(/\s+/)[0] || "";

// Trove Next's list endpoint has no "fetch by id" variant and no total count -
// a generous limit and no status filter is the simplest way to cover every
// profile a single custodian's account picker could ever need to resolve.
const normalizeTroveNextProfiles = (response) => {
    if (Array.isArray(response)) return response;
    const candidates = [response?.data, response?.profiles, response?.data?.profiles, response?.data?.data];
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }
    return [];
};

const AccountList = props => {

    const accountIdFromPath = props.location.pathname?.split("/")[5];
    const accounts = useSelector(({ selectedUser }) => selectedUser.data.accounts, shallowEqual);

    const hasTroveNextAccounts = useMemo(
        _ => accounts?.some(acc => !!getTroveNextProfileId(acc)) ?? false,
        [accounts]
    );
    const { data: troveNextProfilesResponse } = useGetTroveNextProfilesQuery(
        { query: { limit: 500 } },
        { skip: !hasTroveNextAccounts }
    );
    const troveNextNameByProfileId = useMemo(() => {
        const map = {};
        normalizeTroveNextProfiles(troveNextProfilesResponse).forEach(profile => {
            const id = getTroveNextProfileRecordId(profile);
            const name = getTroveNextProfileRealName(profile);
            if (id && name) map[id] = name;
        });
        return map;
    }, [troveNextProfilesResponse]);

    const accountOptions = useMemo(
        _ =>
            accounts?.map(acc => {
                const userType = acc?.userType ?? acc?.details?.userType;
                const accountManagementType = acc?.accountManagementType ?? acc?.details?.accountManagementType;
                const troveNextProfileId = getTroveNextProfileId(acc);
                const isTroveNext =
                    !!troveNextProfileId ||
                    userType === "MINOR" ||
                    userType === "CUSTODIAN" ||
                    userType === "CUSTODIAL" ||
                    accountManagementType === "CUSTODIAN" ||
                    accountManagementType === "CUSTODIAL";
                const providerLabel = `${acc?.partner ?? acc?.provider ?? ""}`.trim().toLowerCase();
                const idLabel = acc?.accountNumber?.substr(0, 12) ?? acc?.accountId ?? "";
                const statusSuffix = acc?.status === "active" ? "" : `-${`${acc?.status ?? ""}`.trim().toLowerCase()}`;
                const baseLabel = `${providerLabel}-${idLabel}${statusSuffix}`.replace(/_/g, "-");
                const isCorporate = !isTroveNext && !!getCorporateApplicationId(acc);
                const corporateName = isCorporate ? `${acc?.name || acc?.details?.name || ""}`.trim() : "";
                // Corporate: "<account name> - alpaca-411510055", so the company is
                // visible and searchable in the picker.
                const label = isTroveNext
                    ? `trvNext-${baseLabel}`
                    : (corporateName ? `${corporateName} - ${baseLabel}` : baseLabel);
                const troveNextRealName =
                    getTroveNextBeneficiaryName(acc)
                    || (troveNextProfileId ? troveNextNameByProfileId[troveNextProfileId] : "");
                const productLabel = acc?.name || acc?.details?.name || baseLabel;
                const productSuffix = getProductSuffix(productLabel);
                const beneficiaryFirstName = getFirstName(troveNextRealName);
                const displayName = (beneficiaryFirstName && productSuffix)
                    ? `${beneficiaryFirstName} (${productSuffix})`
                    : (troveNextRealName ? `${productLabel} — ${troveNextRealName}` : productLabel);
                return { value: acc.accountId, label, baseLabel, displayName, isTroveNext, isCorporate, corporateName, _raw: acc };
            }) ?? [],
        [accounts, troveNextNameByProfileId]
    );
    const [selectedAccount, setSeletectedAccount] = useState(accountOptions.filter(acc => acc.value === accountIdFromPath)[0]);
    const accountId = accountIdFromPath ?? selectedAccount?.value;

    const accountsOrder = {
        wallet: 10, dw: 9, zanibal: 8, paper: -2
    }

    accountOptions.sort((a, b) => {
        const aName = a.label.split("-")[0].toLowerCase()
        const bName = b.label.split("-")[0].toLowerCase()
        return (accountsOrder[bName] || 1) - (accountsOrder[aName] || 1)
    })

    // Redux dispatch
    const dispatch = useDispatch();

    const switchAccount = val => {
        setSeletectedAccount(val);
        if (val) {
            props.history.push(`/users/view/${props.match.params.userId}/account-information/${val.value}/${props.location.pathname?.split("/")[6] ?? "overview"}`);
        }
    }
    /* Fetch User Account List if not in redux */
    useEffect(() => {
        if (!accounts) {
            dispatch(handlers.fetchUserAccounts(props.match.params.userId));
        }
    }, [dispatch, props.showAccountList, props.match.params.userId, accounts]);

    // Auto select account in select option if not selected
    useEffect(_ => {
        if (accountIdFromPath && !selectedAccount && accountOptions && accountOptions?.length > 0) {
            const acc = accountOptions.filter(acc => acc.value === accountIdFromPath)[0];
            if (acc) setSeletectedAccount(acc);
        }
    }, [selectedAccount, accountIdFromPath, accountOptions]);

    return <div className="w-100">
        {
            <>
                <div className="mt-2">
                    <Select
                        value={selectedAccount}
                        onChange={switchAccount}
                        options={accountOptions}
                        isSearchable={true}
                        isClearable={true}
                        isLoading={!accounts}
                        placeholder="Select Account"
                        formatOptionLabel={(opt) => {
                            const isTroveNext = !!opt?.isTroveNext;
                            const label = opt?.label ?? "";
                            if (opt?.isCorporate && opt?.corporateName) {
                                return (
                                    <span className="d-flex align-items-center justify-content-between w-100" style={{ gap: 10 }}>
                                        <span
                                            style={{
                                                flex: "1 1 auto",
                                                minWidth: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={label}
                                        >
                                            <span className="font-weight-bold">{opt.corporateName}</span>
                                            {` - ${opt.baseLabel}`}
                                        </span>
                                        <span
                                            className="label label-sm label-light-primary label-inline"
                                            style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                        >
                                            Corporate
                                        </span>
                                    </span>
                                );
                            }
                            if (!isTroveNext) return label;
                            const base = opt?.displayName ?? opt?.baseLabel ?? label.replace(/^trvNext-/, "");
                            return (
                                <span className="d-flex align-items-center justify-content-between w-100" style={{ gap: 10 }}>
                                    <span
                                        style={{
                                            flex: "1 1 auto",
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                        title={base}
                                    >
                                        {base}
                                    </span>
                                    <span
                                        className="label label-sm label-light-success label-inline"
                                        style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                    >
                                        Next Gen
                                    </span>
                                </span>
                            );
                        }}
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderColor: "hsl(0, 0%, 80%)",
                                boxShadow: 0,
                                '&:hover': {
                                    borderColor: "hsl(0, 0%, 80%)",
                                }
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                paddingRight: 4,
                            }),
                            singleValue: (base) => ({
                                ...base,
                                width: "calc(100% - 8px)",
                                maxWidth: "calc(100% - 8px)",
                            }),
                        }}
                    />

                </div>

                {
                    accountId && <div className={`pl-1 pt-3`}>
                        <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/overview`} className="navi-link py-2 my-1">
                            <span className="navi-text font-size-lg">
                                <span className="navi-icon mr-3">
                                    <span className="svg-icon" >
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" >
                                            <g id="Stockholm-icons-/-Design-/-Interselect" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <rect id="bound" x="0" y="0" width="24" height="24"></rect>
                                                <path d="M6,9 L6,15 C6,16.6568542 7.34314575,18 9,18 L15,18 L15,18.8181818 C15,20.2324881 14.2324881,21 12.8181818,21 L5.18181818,21 C3.76751186,21 3,20.2324881 3,18.8181818 L3,11.1818182 C3,9.76751186 3.76751186,9 5.18181818,9 L6,9 Z M17,16 L17,10 C17,8.34314575 15.6568542,7 14,7 L8,7 L8,6.18181818 C8,4.76751186 8.76751186,4 10.1818182,4 L17.8181818,4 C19.2324881,4 20,4.76751186 20,6.18181818 L20,13.8181818 C20,15.2324881 19.2324881,16 17.8181818,16 L17,16 Z" id="Combined-Shape" fill="#000000" fillRule="nonzero" opacity="0.3"></path>
                                                <path d="M9.27272727,9 L13.7272727,9 C14.5522847,9 15,9.44771525 15,10.2727273 L15,14.7272727 C15,15.5522847 14.5522847,16 13.7272727,16 L9.27272727,16 C8.44771525,16 8,15.5522847 8,14.7272727 L8,10.2727273 C8,9.44771525 8.44771525,9 9.27272727,9 Z" id="Rectangle-19-Copy-3" fill="#000000"></path>
                                            </g>
                                        </svg>
                                    </span>
                                </span>
                                Overview</span>
                        </NavLink>
                        <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/positions`} className="navi-link py-2 my-1">
                            <span className="navi-text font-size-lg">
                                <span className="navi-icon mr-3">
                                    <span className="svg-icon" >
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" >
                                            <g id="Stockholm-icons-/-Shopping-/-Box2" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <rect id="bound" x="0" y="0" width="24" height="24"></rect>
                                                <path d="M4,9.67471899 L10.880262,13.6470401 C10.9543486,13.689814 11.0320333,13.7207107 11.1111111,13.740321 L11.1111111,21.4444444 L4.49070127,17.526473 C4.18655139,17.3464765 4,17.0193034 4,16.6658832 L4,9.67471899 Z M20,9.56911707 L20,16.6658832 C20,17.0193034 19.8134486,17.3464765 19.5092987,17.526473 L12.8888889,21.4444444 L12.8888889,13.6728275 C12.9050191,13.6647696 12.9210067,13.6561758 12.9368301,13.6470401 L20,9.56911707 Z" id="Combined-Shape" fill="#000000"></path>
                                                <path d="M4.21611835,7.74669402 C4.30015839,7.64056877 4.40623188,7.55087574 4.5299008,7.48500698 L11.5299008,3.75665466 C11.8237589,3.60013944 12.1762411,3.60013944 12.4700992,3.75665466 L19.4700992,7.48500698 C19.5654307,7.53578262 19.6503066,7.60071528 19.7226939,7.67641889 L12.0479413,12.1074394 C11.9974761,12.1365754 11.9509488,12.1699127 11.9085461,12.2067543 C11.8661433,12.1699127 11.819616,12.1365754 11.7691509,12.1074394 L4.21611835,7.74669402 Z" id="Path" fill="#000000" opacity="0.3"></path>
                                            </g>
                                        </svg>
                                    </span>
                                </span>
                                Positions</span>
                        </NavLink>
                        <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/orders`} className="navi-link py-2 my-1">
                            <span className="navi-text font-size-lg">
                                <span className="navi-icon mr-3">
                                    <span className="svg-icon" >
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" >
                                            <g id="Stockholm-icons-/-Shopping-/-Cart2" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <rect id="bound" x="0" y="0" width="24" height="24"></rect>
                                                <path d="M12,4.56204994 L7.76822128,9.6401844 C7.4146572,10.0644613 6.7840925,10.1217854 6.3598156,9.76822128 C5.9355387,9.4146572 5.87821464,8.7840925 6.23177872,8.3598156 L11.2317787,2.3598156 C11.6315738,1.88006147 12.3684262,1.88006147 12.7682213,2.3598156 L17.7682213,8.3598156 C18.1217854,8.7840925 18.0644613,9.4146572 17.6401844,9.76822128 C17.2159075,10.1217854 16.5853428,10.0644613 16.2317787,9.6401844 L12,4.56204994 Z" id="Path-30" fill="#000000" fillRule="nonzero" opacity="0.3"></path>
                                                <path d="M3.28077641,9 L20.7192236,9 C21.2715083,9 21.7192236,9.44771525 21.7192236,10 C21.7192236,10.0817618 21.7091962,10.163215 21.6893661,10.2425356 L19.5680983,18.7276069 C19.234223,20.0631079 18.0342737,21 16.6576708,21 L7.34232922,21 C5.96572629,21 4.76577697,20.0631079 4.43190172,18.7276069 L2.31063391,10.2425356 C2.17668518,9.70674072 2.50244587,9.16380623 3.03824078,9.0298575 C3.11756139,9.01002735 3.1990146,9 3.28077641,9 Z M12,12 C11.4477153,12 11,12.4477153 11,13 L11,17 C11,17.5522847 11.4477153,18 12,18 C12.5522847,18 13,17.5522847 13,17 L13,13 C13,12.4477153 12.5522847,12 12,12 Z M6.96472382,12.1362967 C6.43125772,12.2792385 6.11467523,12.8275755 6.25761704,13.3610416 L7.29289322,17.2247449 C7.43583503,17.758211 7.98417199,18.0747935 8.51763809,17.9318517 C9.05110419,17.7889098 9.36768668,17.2405729 9.22474487,16.7071068 L8.18946869,12.8434035 C8.04652688,12.3099374 7.49818992,11.9933549 6.96472382,12.1362967 Z M17.0352762,12.1362967 C16.5018101,11.9933549 15.9534731,12.3099374 15.8105313,12.8434035 L14.7752551,16.7071068 C14.6323133,17.2405729 14.9488958,17.7889098 15.4823619,17.9318517 C16.015828,18.0747935 16.564165,17.758211 16.7071068,17.2247449 L17.742383,13.3610416 C17.8853248,12.8275755 17.5687423,12.2792385 17.0352762,12.1362967 Z" id="Combined-Shape" fill="#000000"></path>
                                            </g>
                                        </svg>
                                    </span>
                                </span>
                                Orders</span>
                        </NavLink>
                        <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/transactions`} className="navi-link py-2 my-1">
                            <span className="navi-text font-size-lg">
                                <span className="navi-icon mr-3">
                                    <span className="svg-icon" >
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                            <g id="Stockholm-icons-/-General-/-Expand-arrows" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <polygon id="Bound" points="0 0 24 0 24 24 0 24"></polygon>
                                                <path d="M10.5857864,12 L5.46446609,6.87867966 C5.0739418,6.48815536 5.0739418,5.85499039 5.46446609,5.46446609 C5.85499039,5.0739418 6.48815536,5.0739418 6.87867966,5.46446609 L12,10.5857864 L18.1923882,4.39339828 C18.5829124,4.00287399 19.2160774,4.00287399 19.6066017,4.39339828 C19.997126,4.78392257 19.997126,5.41708755 19.6066017,5.80761184 L13.4142136,12 L19.6066017,18.1923882 C19.997126,18.5829124 19.997126,19.2160774 19.6066017,19.6066017 C19.2160774,19.997126 18.5829124,19.997126 18.1923882,19.6066017 L12,13.4142136 L6.87867966,18.5355339 C6.48815536,18.9260582 5.85499039,18.9260582 5.46446609,18.5355339 C5.0739418,18.1450096 5.0739418,17.5118446 5.46446609,17.1213203 L10.5857864,12 Z" id="Combined-Shape" fill="#000000" opacity="0.3" transform="translate(12.535534, 12.000000) rotate(-360.000000) translate(-12.535534, -12.000000) "></path>
                                                <path d="M6,18 L9,18 C9.66666667,18.1143819 10,18.4477153 10,19 C10,19.5522847 9.66666667,19.8856181 9,20 L4,20 L4,15 C4,14.3333333 4.33333333,14 5,14 C5.66666667,14 6,14.3333333 6,15 L6,18 Z M18,18 L18,15 C18.1143819,14.3333333 18.4477153,14 19,14 C19.5522847,14 19.8856181,14.3333333 20,15 L20,20 L15,20 C14.3333333,20 14,19.6666667 14,19 C14,18.3333333 14.3333333,18 15,18 L18,18 Z M18,6 L15,6 C14.3333333,5.88561808 14,5.55228475 14,5 C14,4.44771525 14.3333333,4.11438192 15,4 L20,4 L20,9 C20,9.66666667 19.6666667,10 19,10 C18.3333333,10 18,9.66666667 18,9 L18,6 Z M6,6 L6,9 C5.88561808,9.66666667 5.55228475,10 5,10 C4.44771525,10 4.11438192,9.66666667 4,9 L4,4 L9,4 C9.66666667,4 10,4.33333333 10,5 C10,5.66666667 9.66666667,6 9,6 L6,6 Z" id="Combined-Shape" fill="#000000" fillRule="nonzero"></path>
                                            </g>
                                        </svg>
                                    </span>
                                </span>
                                Transactions</span>
                        </NavLink>
                        <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/payments`} className="navi-link py-2 my-1">
                            <span className="navi-text font-size-lg">
                                <span className="navi-icon mr-3">
                                    <span className="svg-icon" >
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                            <g id="Stockholm-icons-/-Shopping-/-Gift" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <rect id="bound" x="0" y="0" width="24" height="24"></rect>
                                                <path d="M4,6 L20,6 C20.5522847,6 21,6.44771525 21,7 L21,8 C21,8.55228475 20.5522847,9 20,9 L4,9 C3.44771525,9 3,8.55228475 3,8 L3,7 C3,6.44771525 3.44771525,6 4,6 Z M5,11 L10,11 C10.5522847,11 11,11.4477153 11,12 L11,19 C11,19.5522847 10.5522847,20 10,20 L5,20 C4.44771525,20 4,19.5522847 4,19 L4,12 C4,11.4477153 4.44771525,11 5,11 Z M14,11 L19,11 C19.5522847,11 20,11.4477153 20,12 L20,19 C20,19.5522847 19.5522847,20 19,20 L14,20 C13.4477153,20 13,19.5522847 13,19 L13,12 C13,11.4477153 13.4477153,11 14,11 Z" id="Combined-Shape" fill="#000000"></path>
                                                <path d="M14.4452998,2.16794971 C14.9048285,1.86159725 15.5256978,1.98577112 15.8320503,2.4452998 C16.1384028,2.90482849 16.0142289,3.52569784 15.5547002,3.83205029 L12,6.20185043 L8.4452998,3.83205029 C7.98577112,3.52569784 7.86159725,2.90482849 8.16794971,2.4452998 C8.47430216,1.98577112 9.09517151,1.86159725 9.5547002,2.16794971 L12,3.79814957 L14.4452998,2.16794971 Z" id="Path-31" fill="#000000" fillRule="nonzero" opacity="0.3"></path>
                                            </g>
                                        </svg>
                                    </span>
                                </span>
                                Payments</span>
                        </NavLink>
                        <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/statements`} className="navi-link py-2 my-1">
                            <span className="navi-text font-size-lg">
                                <span className="navi-icon mr-3">
                                    <span className="svg-icon" >
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" >
                                            <g id="Stockholm-icons-/-Files-/-File" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <polygon id="Shape" points="0 0 24 0 24 24 0 24"></polygon>
                                                <path d="M5.85714286,2 L13.7364114,2 C14.0910962,2 14.4343066,2.12568431 14.7051108,2.35473959 L19.4686994,6.3839416 C19.8056532,6.66894833 20,7.08787823 20,7.52920201 L20,20.0833333 C20,21.8738751 19.9795521,22 18.1428571,22 L5.85714286,22 C4.02044787,22 4,21.8738751 4,20.0833333 L4,3.91666667 C4,2.12612489 4.02044787,2 5.85714286,2 Z" id="Combined-Shape" fill="#000000" fillRule="nonzero" opacity="0.3"></path>
                                                <rect id="Rectangle" fill="#000000" x="6" y="11" width="9" height="2" rx="1"></rect>
                                                <rect id="Rectangle-Copy" fill="#000000" x="6" y="15" width="5" height="2" rx="1"></rect>
                                            </g>
                                        </svg>
                                    </span>
                                </span>
                                Statements</span>
                        </NavLink>
                        <Permission permission={Permissions.ACCOUNT_META}>
                            <NavLink to={`/users/view/${props.match.params.userId}/account-information/${accountId}/meta`} className="navi-link py-2 my-1">
                                <span className="navi-text font-size-lg">
                                    <span className="navi-icon mr-3">
                                        <span className="svg-icon" >
                                            <SVG src={toAbsoluteUrl("/media/svg/icons/Text/Code.svg")} />
                                        </span>
                                    </span>
                                    Meta</span>
                            </NavLink>
                        </Permission>
                    </div>
                }
            </>
        }
    </div>
}

export default AccountList;
